import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { requiresEmailVerification } from './authVerification';
import './css/Login.css';
import ResetPasswordDialog from './ResetPasswordDialog';
import { ROUTES } from './routes';
import { guideReturnPath } from './guide-bot/authReturn';

const oauthApiBaseUrl = process.env.REACT_APP_JOURNEYBOT_API_URL;
const pendingOAuthRequestKey = 'journeybot.pendingOAuthRequest';

function LoginComponent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const oauthHandoffRequest = useRef(null);
  const resetPasswordTrigger = useRef(null);
  const { authState, loginWithEmail, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const guideReturn = guideReturnPath(location.search);

  const handleLogin = async (event) => {
    event.preventDefault();
    await loginWithEmail(email, password);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const requestFromUrl = searchParams.get('oauth_request');
    if (requestFromUrl) window.sessionStorage?.setItem(pendingOAuthRequestKey, requestFromUrl);
    const oauthRequest = requestFromUrl || window.sessionStorage?.getItem(pendingOAuthRequestKey);

    if (authState.user && requiresEmailVerification(authState.user)) {
      navigate(ROUTES.verifyEmail, {
        replace: true,
        state: { from: location.state?.from || { pathname: guideReturn || ROUTES.mainView } },
      });
      return;
    }

    if (authState.user && oauthRequest) {
      if (oauthHandoffRequest.current === oauthRequest) return;
      oauthHandoffRequest.current = oauthRequest;

      void authState.user.getIdToken(true).then((idToken) => fetch(`${oauthApiBaseUrl}/oauth/session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oauth_request: oauthRequest }),
      })).then(async (response) => {
        const body = await response.json();
        if (!response.ok || typeof body.continue_url !== 'string') {
          throw new Error(body.error_description || 'Unable to resume JourneyBot authorization.');
        }
        window.sessionStorage?.removeItem(pendingOAuthRequestKey);
        window.location.assign(body.continue_url);
      }).catch((error) => {
        if (oauthHandoffRequest.current === oauthRequest) oauthHandoffRequest.current = null;
        console.error('JourneyBot OAuth login handoff failed', error);
      });
      return;
    }

    if (authState.loading === false && authState.user) {
      const returnPath = location.state?.from?.pathname || guideReturn;
      const safeReturnPath = typeof returnPath === 'string' && returnPath.startsWith('/') && !returnPath.startsWith('//')
        ? `${returnPath}${location.state?.from?.search || ''}`
        : ROUTES.mainView;
      navigate(safeReturnPath, {
        replace: true,
        state: location.state?.useTemplate ? { useTemplate: location.state.useTemplate } : undefined,
      });
    }
  }, [authState, location.state, navigate, guideReturn]);

  return (
    <div className="login" data-testid="login-page">
      <Box className="login__container" component="section" aria-labelledby="sign-in-title">
        <Stack component="form" spacing={1.5} onSubmit={handleLogin}>
          <Typography id="sign-in-title" component="h1" variant="h4">
            Sign in
          </Typography>
          <TextField
            id="login-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            label="Email address"
            autoComplete="email"
            required
            fullWidth
            inputProps={{ 'data-testid': 'login-email' }}
          />
          <TextField
            id="login-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            label="Password"
            autoComplete="current-password"
            required
            fullWidth
            inputProps={{ 'data-testid': 'login-password' }}
          />
          <Box sx={{ alignSelf: 'flex-end', mt: '-4px !important' }}>
            <MuiLink
              component="button"
              type="button"
              ref={resetPasswordTrigger}
              onClick={() => setResetPasswordOpen(true)}
              data-testid="forgot-password"
            >
              Forgot password?
            </MuiLink>
          </Box>
          <Button className="login__btn" type="submit" variant="contained" data-testid="login-submit">
            Login
          </Button>
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Button
            className="login__btn login__google"
            type="button"
            variant="outlined"
            onClick={loginWithGoogle}
            data-testid="login-google"
          >
            Sign in with Google
          </Button>
          <Typography variant="body2">
            Don&apos;t have an account? <RouterLink to={guideReturn ? `${ROUTES.register}?returnTo=%2Fguide-the-bot` : ROUTES.register}>Register</RouterLink> now.
          </Typography>
        </Stack>
        <ResetPasswordDialog
          open={resetPasswordOpen}
          email={email}
          onEmailChange={setEmail}
          onClose={() => setResetPasswordOpen(false)}
        />
      </Box>
    </div>
  );
}

export { LoginComponent };
